import { colors } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, Ref } from 'react'
import { RemoteControls, RemoteHeader } from './remote-controls'

/**
 * A size container, so the column inside can be as wide as the 16:9 picture
 * that fits the stage's height.
 */
const Stage = styled.div`
	container-type: size;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-grow: 1;
	min-height: 0;
	min-width: 0;
	width: 100%;
	padding: 10px 0;
`

/** The header, the gaps and the controls: what is not the picture. */
const CHROME_HEIGHT = '112px'

const Column = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: min(100%, (100cqh - ${CHROME_HEIGHT}) * 16 / 9);
`

/**
 * Only a picture: the trainer works the film through the remote under it,
 * never through the frame (a click in it would pause this player alone).
 */
const Player = styled.div`
	width: 100%;
	aspect-ratio: 16 / 9;
	border-radius: 12px;
	overflow: hidden;
	background: ${colors.black};
	pointer-events: none;

	iframe {
		display: block;
		width: 100%;
		height: 100%;
		border: 0;
	}
`

type Props = {
	name: string
	/** The element the monitor's player is built into. */
	playerRef: Ref<HTMLDivElement>
	playing: boolean
	positionMs: number
	durationMs: number
	onPlayPause: () => void
	onSeek: (positionMs: number) => void
	onClose: () => void
}

/**
 * The console's view of a film playing on the projector: the picture as large
 * as the stage allows - silent, the projector's speakers play the sound -
 * and the remote right under it. The player in the picture is built by
 * `useVideoMonitor`.
 */
export const VideoMonitor: FC<Props> = ({
	name,
	playerRef,
	onClose,
	...controls
}) => (
	<Stage>
		<Column data-testid="presenter.media_remote" data-kind="youtube">
			<RemoteHeader name={name} singleLine onClose={onClose} />
			<Player
				ref={playerRef}
				aria-hidden="true"
				data-testid="presenter.video_monitor.player"
			/>
			<RemoteControls name={name} {...controls} />
		</Column>
	</Stage>
)
